// Session stems export: renders each audio track in isolation (with its own FX/
// pan/fader, pre master bus) to a full-length, time-aligned 24-bit WAV, then zips
// the stems together with a human-readable session sheet. This is the practical
// interchange for moving a session into another DAW/NLE — drop every stem at
// 00:00 and the arrangement lines up. Rendering is chunked and the ZIP is
// streamed, so long sessions don't need one huge buffer.
import { Project, clipSpeed } from "../state/model";
import { renderChunks, projectAudioEnd } from "./render";
import { wavHeader, pcmBytes } from "./export-wav";
import { fmtTime } from "../util";
import { ZipWriter } from "./zip";

const sanitize = (s: string) => s.replace(/[^\w\-]+/g, "_") || "track";

export async function exportStems(
  project: Project,
  buffers: Map<string, AudioBuffer>,
  sampleRate = 48000,
  onProgress?: (frac: number) => void,
): Promise<Blob> {
  const lengthSec = Math.max(1, projectAudioEnd(project));
  const zip = new ZipWriter();
  const used = new Set<string>();
  const tracks = project.tracks.filter((t) => t.kind !== "video" && t.clips.some((c) => c.takeActive !== false && !c.disabled && buffers.get(c.assetId)));

  let edl = `WebCut session stems\n` +
    `====================\n` +
    `Project:     ${project.name}\n` +
    `Video:       ${project.width}×${project.height} @ ${project.fps} fps\n` +
    `Sample rate: ${sampleRate} Hz, 24-bit WAV, stereo\n` +
    `Length:      ${fmtTime(lengthSec)} (${lengthSec.toFixed(3)} s)\n\n` +
    `All stems are the full session length and aligned to 00:00.\n` +
    `Import them onto separate tracks starting at the session start and the\n` +
    `arrangement will line up exactly. Stems are printed post per-track FX,\n` +
    `pan and fader, but pre master bus. Muted tracks are included (marked).\n\n` +
    `Clip map (start - end  name):\n`;

  let idx = 1;
  for (const track of tracks) {
    const frames = Math.round(lengthSec * sampleRate);
    const parts: BlobPart[] = [wavHeader(frames, 2, sampleRate, 24) as BlobPart];
    await renderChunks(project, buffers, { sampleRate, start: 0, end: lengthSec, tracks: [track], master: false,
      onProgress: (f) => onProgress?.((idx - 1 + f) / tracks.length) }, (buf) => { parts.push(pcmBytes(buf, 24, 2) as BlobPart); });
    let name = `${String(idx).padStart(2, "0")}_${sanitize(track.name)}`;
    while (used.has(name)) name += "_";
    used.add(name);
    await zip.add(`stems/${name}.wav`, new Blob(parts, { type: "audio/wav" }));

    edl += `\nTrack ${idx}: ${track.name}  (vol ${track.volume.toFixed(2)}, pan ${track.pan.toFixed(2)}${track.mute ? ", MUTED" : ""})\n`;
    for (const c of track.clips) {
      if (c.takeActive === false || c.disabled) continue;
      const sp = clipSpeed(c);
      edl += `   ${fmtTime(c.start)} - ${fmtTime(c.start + c.duration)}  ${c.name}${sp !== 1 ? `  (speed ${Math.round(sp * 100)}%)` : ""}\n`;
    }
    idx++;
  }

  if (idx === 1) edl += `\n(No audio clips to export.)\n`;

  await zip.add("session.txt", edl);
  await zip.add("README.txt",
    `These are time-aligned session stems exported from WebCut.\n\n` +
    `How to use:\n` +
    `1. Create one track per WAV in your DAW or NLE.\n` +
    `2. Import each WAV at the session start (00:00 / bar 1).\n` +
    `3. Everything will be in sync; mix from there.\n\n` +
    `See session.txt for the clip map and per-track gain/pan.\n\n` +
    `Why stems instead of OMF/AAF? Those are legacy binary interchange\n` +
    `containers; time-aligned stems import reliably into any DAW or NLE.\n`);
  return zip.finish();
}
